/** Exact active-store reads and mutations. A scope switch cannot publish an obsolete response. */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { shopifyQueryPolicies } from '../queries/shopifyQueryPolicies'
import { useShopifyTrackedMutation } from './useShopifyTrackedMutation'
import {
  knowledgeReadInputSchema,
  storeKnowledgeReadSchema,
  knowledgeAnswerRecordSchema,
  type KnowledgeReadInput,
  type StoreKnowledgeWrite,
} from '@testfeed/integration-contracts'
import { storeKnowledgeCopy as copy } from '@testfeed/product-language'
import { useShopifyAppContext, buildShopifyAppScopeKey, shopifyQueryKey } from '../ShopifyAppContext'
import { throwMerchantFacingApiError } from '../shopifyApiError'
import { parseContractPayload } from '../merchantErrors'
import { shopifyApiClient } from '../shopifyApiClient'
import type { ShopifyTokenSource } from '../shopifyApiFetch'
import type { z } from 'zod'
async function readResponse<T>(response: Pick<Response, 'ok' | 'status' | 'json'>, schema: z.ZodType<T>) {
  if (!response.ok) await throwMerchantFacingApiError(response, 'store-knowledge')
  return parseContractPayload('store-knowledge-response', schema, await response.json())
}
export function useStoreKnowledge(shopify: ShopifyTokenSource, partial: Partial<KnowledgeReadInput>) {
  const scope = useShopifyAppContext()
  const key = buildShopifyAppScopeKey(scope)
  const active = () => !scope.isActiveScope || scope.isActiveScope(key)
  const enabled = Boolean(scope.orgId && scope.connectionId && active())
  const input = parseContractPayload('store-knowledge-read-input', knowledgeReadInputSchema, partial)
  return useQuery({
    ...shopifyQueryPolicies.storeKnowledge,
    queryKey: shopifyQueryKey(scope.shopDomain, scope.orgId, scope.connectionId, ['store-knowledge', input]),
    enabled,
    retry: false,
    queryFn: async ({ signal }) => {
      if (!active()) throw Error(copy.failed)
      const response = await shopifyApiClient(shopify, undefined, {
        onPhase: () => {
          if (!active()) throw Error(copy.failed)
        },
      }).api['store-knowledge'].read.$post(
        { json: input },
        { init: { signal }, headers: { 'X-TestFeed-Integration-Connection-Id': scope.connectionId! } },
      )
      const data = await readResponse(response, storeKnowledgeReadSchema)
      if (!active() || data.organizationId !== scope.orgId || data.connectionId !== scope.connectionId)
        throw Error(copy.failed)
      return data
    },
  })
}
export function useSaveStoreKnowledge(shopify: ShopifyTokenSource) {
  const scope = useShopifyAppContext()
  const key = buildShopifyAppScopeKey(scope)
  const client = useQueryClient()
  const active = () => Boolean(scope.orgId && scope.connectionId && (!scope.isActiveScope || scope.isActiveScope(key)))
  return useShopifyTrackedMutation(shopify, 'save-knowledge', {
    mutationFn: async (input: StoreKnowledgeWrite) => {
      if (!active()) throw Error(copy.saveFailed)
      const response = await shopifyApiClient(shopify, undefined, {
        onPhase: () => {
          if (!active()) throw Error(copy.saveFailed)
        },
      }).api['store-knowledge'].answer.$post(
        { json: input },
        { headers: { 'X-TestFeed-Integration-Connection-Id': scope.connectionId! } },
      )
      const result = await readResponse(response, knowledgeAnswerRecordSchema)
      if (!active()) throw Error(copy.saveFailed)
      return result
    },
    onSuccess: () => {
      if (active())
        return client.invalidateQueries({
          queryKey: shopifyQueryKey(scope.shopDomain, scope.orgId, scope.connectionId, ['store-knowledge']),
        })
    },
  })
}
